import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {FileUpload} from 'primeng/fileupload';
import {ToastService} from '../../services/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {SiFile} from '../../models/file.model';
import {GenericFunctionsUtil} from '../../utils/generic-functions.util';
import {ValidationService} from '../../services/validation.service';

@Component({
  selector: 'si-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FileUploadComponent implements OnInit {
  @Input() id: string;
  @Input() name: string;
  @Input() text: string;
  @Input() subtext: string;
  @Input() dragText = 'CORE_DRAG_FILE';
  @Input() browseText = 'CORE_BROWSE_HERE';
  @Input() styleClass: string;
  @Input() image: string;
  @Input() imageError: string;
  @Input() disabled: boolean;
  @Input() hideLabel = false;
  @Input() tooltipText: string;
  @Input() tooltipPosition: string;
  @Input() tooltipIcon: string;
  @Input() tooltipStyleClass: string;
  @Input() tooltipEscape = true;
  @Input() forceShowTooltipIcon: boolean;
  @Input() rule: string;
  @Input() validationExternalErrorText: string;
  @Input() multiple = false;
  @Input() fileLimit: number;
  @Input() maxFileSize = 10485760; // 10Mb
  @Input() previewWidth = 50;
  @Input() accept: string;
  @Input() path: string;
  @Output() modelChange = new EventEmitter<any>();
  @Output() tooltipIconClick = new EventEmitter<any>();
  @ViewChild(FileUpload, {static: true}) pFileUpload: FileUpload;
  @ContentChild('pTemplateFile') pTemplateFile: TemplateRef<ElementRef>;
  showRuleErrorOutsideIcon: boolean;
  validationErrorText: string;
  maximumSizeText: string;
  fileAcceptText: string;
  showUploadError: boolean;
  private _model: (File | SiFile)[];

  constructor(
    private toastService: ToastService,
    private translateService: TranslateService,
    private validationService: ValidationService
  ) {
    this.setLogic();
  }

  get model() {
    return this._model;
  }

  @Input()
  set model(val: any[]) {
    this._model = [];

    if (val) {
      // We have to make a copy to convert the FileList to a simple array as it is readonly.
      for (const file of val) {
        if (GenericFunctionsUtil.isSiFile(file) && !file.url && file.fileId) {
          file.url = `${this.path}/${file.fileId}`;
        }

        this._model.push(file);
      }
    }
  }

  ngOnInit(): void {
    this.decorateFileUpload();
  }

  setLogic(): void {
    this.showRuleErrorOutsideIcon = this.validationService.getShowRuleErrorOutsideIcon();
  }

  onDragEnter(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  onDragOver(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.isFileLimitExceeded(event)) {
      return;
    }

    this.pFileUpload.onDrop(event);
  }

  onFileSelect(event): void {
    if (this.isFileLimitExceeded(event)) {
      event.target.value = '';
      return;
    }

    this.pFileUpload.onFileSelect(event);
  }

  isFileLimitExceeded(event): boolean {
    if (this.multiple && this.fileLimit) {
      const newFiles = event.dataTransfer ? event.dataTransfer.files : event.target.files;
      if (this.model?.length + newFiles.length > this.fileLimit) {
        this.showUploadError = true;
        this.toastService.error(
          this.translateService.instant('CORE_UPLOAD_FILE_LIMIT_ERROR', {fileLimit: this.fileLimit}),
        );

        return true;
      }
    }

    return false;
  }

  onAddFile(event): void {
    this.model = event.currentFiles;
    this.modelChange.emit(this.model);
  }

  onClickImagePreview(file: File | SiFile): void {
    this.openFile(file);
  }

  onClickFile(file: File | SiFile): void {
    this.downloadFile(file);
  }

  // Open the file in a new tab.
  openFile(file: File | SiFile): void {
    window.open(this.getUrl(file), '_blank');
  }

  // Open the browser explorer to download the file. The file has to be in same server.
  downloadFile(file: File | SiFile): void {
    const fileUrl = this.getUrl(file);
    const fileLink = document.createElement('a');
    fileLink.href = fileUrl;
    fileLink.download = file.name;
    fileLink.click();
    if (!GenericFunctionsUtil.isSiFile(file)) {
      URL.revokeObjectURL(fileUrl);
    }
  }

  removeFile(event: Event, index: number): void {
    this.pFileUpload.remove(event, index);
    this.model.splice(index, 1);
    this.modelChange.emit(this.model);
  }

  getUrl(file: File | SiFile): string {
    if (GenericFunctionsUtil.isSiFile(file)) {
      return file.url || `${this.path}/${file.fileId}`;
    }

    return URL.createObjectURL(file);
  }

  // Functions overwriting the real ones in the FileUpload component.
  async decorateFileUpload(): Promise<void> {
    if (this.pFileUpload) {

      // Validate overwritten because we want to set error toasts.
      this.pFileUpload.validate = (file: File | SiFile): boolean => {
        this.showUploadError = false;
        // A SiFile must always be valid for the pFileUpload component.
        if (GenericFunctionsUtil.isSiFile(file)) {
          return true;
        }

        if (this.accept && !this.isFileTypeValid(file)) {
          this.showUploadError = true;
          this.toastService.error(
            this.translateService.instant('CORE_UPLOAD_ALLOWED_TYPES', {allowedTypes: this.accept}),
            'CORE_UPLOAD_TYPE_ERROR'
          );
          return false;
        }

        if (this.maxFileSize && file.size > this.maxFileSize) {
          this.showUploadError = true;
          const maximumSize = this.pFileUpload.formatSize(this.maxFileSize);
          this.toastService.error(
            this.translateService.instant('CORE_UPLOAD_MAX_SIZE_ALLOWED', {maximumSize}),
            'CORE_UPLOAD_SIZE_ERROR'
          );
          return false;
        }

        return true;
      };

      // IsImage overwritten because we do not want a SiFile to validate this function by default.
      const originalIsImage = this.pFileUpload.isImage.bind(this.pFileUpload);
      this.pFileUpload.isImage = (file: File | SiFile): boolean => {
        if (GenericFunctionsUtil.isSiFile(file)) {
          return false;
        }

        return originalIsImage(file);
      };

      this.maximumSizeText = this.translateService.instant('CORE_UPLOAD_MAX_SIZE_ALLOWED_SHORT', {maximumSize: this.pFileUpload.formatSize(this.maxFileSize)});
      this.fileAcceptText = this.getReadableFileTypes(this.accept);
    }
  }

  getReadableFileTypes(accept: string): string {
    return accept?.split(',').map(type => {
      if (type.startsWith('.')) {
        return GenericFunctionsUtil.capitalizeFirstLetter(type.slice(1));
      } else {
        return GenericFunctionsUtil.capitalizeFirstLetter(type);
      }
    }).join(', ');
  }

  isImage(file: File | SiFile): boolean {
    return /^image\//.test(file.type);
  }

  isFileTypeValid(file: File): boolean {
    const acceptableTypes = this.accept.split(',').map(type => type.trim());
    for (let type of acceptableTypes) {
      const acceptable = this.pFileUpload.isWildcard(type)
        ? this.pFileUpload.getTypeClass(file.type) === this.pFileUpload.getTypeClass(type)
        : file.type == type || this.pFileUpload.getFileExtension(file).toLowerCase() === type.toLowerCase();

      if (acceptable) {
        return true;
      }
    }

    return false;
  }

  onClickTooltipIcon(event: any): void {
    this.tooltipIconClick.emit(event);
  }
}
